import { AdministrationHelper } from "@/Administration/Helpers/AdministrationHelper";
import { IUsersTabModel } from "@/Administration/Models/UI/Users/IUsersTabModel";
import { UserEditMode } from "@/Administration/Models/UI/Users/UserEditMode";
import { UsersSubmenuEnum } from "@/Administration/Models/UI/Users/UsersSubmenuEnum";
import { AdministrationStore } from "@/Administration/Stores/AdministrationStore";
import { ClickableTextItem } from "@/Common/Api/ClickableText/Dto/ClickableTextItem";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { EmployeeGroup } from "@/Common/Api/Employee/Dto/EmployeeGroup";
import { EmployeeSearch } from "@/Common/Api/Employee/Dto/EmployeeSearch";
import { OrphanLabForwardDTO } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { EmployeeType } from "@/Common/Models/Employee/Enums/EmployeeType";
import { AuthenticatorCodeDeliveryMethod } from "@/Common/Models/MultifactorAuthentication/AuthenticatorCodeDeliveryMethod";
import { Person } from "@/Common/Models/Person/Person";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { ServerEvent, ServerEventWithThen } from "@/Common/Stores/Actions/ServerEvent";
import { BaseServerOperationHandler } from "@/Common/Stores/Handlers/Server/BaseServerOperationHandler";

class AdministrationUsersResponseHandler extends BaseServerOperationHandler {
    constructor(action: BaseAction) {
        super(action);

        this.RegisterOperation(ServerOperations.Server.GetSearchableEmployees, this.getAllSearchableEmployees);
        this.RegisterOperation(ServerOperations.Server.UpdateActivateEmployee, this.activateUsers);
        this.RegisterOperation(ServerOperations.Server.GetEmployeeInfo, this.getEmployeeInfo);
        this.RegisterOperation(ServerOperations.Server.Myle_SaveProfileImage, this.saveProfileImage, SubServerOperations.Administration.SaveProfileImage);
        this.RegisterOperation(ServerOperations.Server.ARLMYLE_GetOrphanLabForwards, this.getOrphanLabs);
        this.RegisterOperation(ServerOperations.Server.Myle_Admin_UpdateDemographic, this.updateUser);
        this.RegisterOperation(ServerOperations.Server.Myle_Admin_UpdatePractice, this.updateUser);
        this.RegisterOperation(ServerOperations.Server.Myle_Admin_UpdateConfiguration, this.updateConfigurationUser);
        this.RegisterOperation(ServerOperations.Server.Myle_Admin_UpdateTools, this.updateUser);
        this.RegisterOperation(ServerOperations.Server.ARLMYLE_RemoveClinicianFromOrphanLabForwards, this.orphanLabHandler);
        this.RegisterOperation(ServerOperations.Server.ARLMYLE_SaveOrphanLabForwards, this.orphanLabHandler);
        this.RegisterOperation(ServerOperations.Server.UpdateEmployeeType, this.updateEmployeeType);
        this.RegisterOperation(ServerOperations.Server.GetClickableTextSpecificUser, this.getClickableTextSpecificUser);
        this.RegisterOperation(ServerOperations.Server.Create_List_ClickableText, this.createListOfClickableText);
        this.RegisterOperation(ServerOperations.Server.Myle_Admin_CreateEmployee, this.createEmployee);
        this.RegisterOperation(ServerOperations.Server.DeleteProfilePicEmployee, this.deleteProfilePic);
        this.RegisterOperation(ServerOperations.Server.Employee_DeleteClickableTextSection, this.deleteClickableTextsection);
        this.RegisterOperation(ServerOperations.Server.Employee_GetClickableTextCategories, this.getClickableTextForSpecificContext);
        this.RegisterOperation(ServerOperations.Server.Employee_getClickableTextDetails, this.getClickableTextDetails);
        this.RegisterOperation(ServerOperations.Server.Import_ClickableText_List, this.importClickableText);
        this.RegisterOperation(ServerOperations.Server.LoadAllUsersWithClickableText, this.loadAllUsersWithClickableText);
        this.RegisterOperation(ServerOperations.Server.GetClickableTextDetailsSpecificUserSpecificTab, this.getClickableTextDetailsSpecificUserSpecificTab);
        this.RegisterOperation(ServerOperations.Server.Myle_Admin_LoadGroupSetting, this.getGroupInfo);

        this.RegisterOperation(ServerOperations.Server.Myle_Admin_UpdateUser, this.updateUser);
        this.RegisterOperation(ServerOperations.Server.Myle_Employee_Resend_Verification_Code, this.HandleResendEmailVerificationCodeResponse);
    }

    private getAllSearchableEmployees(response: ServerEvent, store: AdministrationStore): void {
        let employeeResponseList: EmployeeSearch[] = response.Data["EmployeeSearchResult"];
        let persons = employeeResponseList.map(x => Person.FromEmployeeSearch(x));

        if (employeeResponseList != null) {
            let usersSearchModel = store.usersTabModel.get("UsersSearchModel");
            usersSearchModel = usersSearchModel.set("SearchEmployeeResult", persons.toImmutable());
            store.usersTabModel = store.usersTabModel.set("UsersSearchModel", usersSearchModel);
        }
    }

    private activateUsers(response: ServerEvent, store: AdministrationStore): void {
        let isSuccess: boolean = response.Data["IsSuccess"];
        let contactId = response.Data["EmployeeContactId"];
        let isInactive = response.Data["IsInactive"];

        if (isSuccess) {
            let usersSearchModel = store.usersTabModel.get("UsersSearchModel");
            usersSearchModel = usersSearchModel.set("IsActiveUser", true);
            let employees = usersSearchModel.get("SearchEmployeeResult").toArray();
            let employee = employees.filter((e: Person) => e.EmployeeSearchModel.Contact_id == contactId).firstOrDefault();

            if (employee != null) {
                employee.EmployeeSearchModel.IsInactive = isInactive;
                let user = cacheStore.GetEmployeeByContactIdOrEmpty(contactId);
                user.IsInactive = isInactive;
                cacheStore.RebindSingleEmployeeToCache(user);
            }
            let employeesImmutable = Immutable.List(employees);
            usersSearchModel = usersSearchModel.set("SearchEmployeeResult", employeesImmutable);
            store.usersTabModel = store.usersTabModel.set("UsersSearchModel", usersSearchModel);

            let selectedEmployee = store.usersTabModel.get("SelectedEmployee");
            if (selectedEmployee) {
                if (selectedEmployee.Model.Contact_id == contactId) {
                    selectedEmployee.Model.IsInactive = isInactive;
                    store.usersTabModel = store.usersTabModel.set("SelectedEmployee", selectedEmployee.clone());
                    store.usersTabModel = store.usersTabModel.set("EditedUser", selectedEmployee.clone());
                }
            }

            const cliniciansAsKVPList = AdministrationHelper.getCliniciansAsKeyValuePairs();

            store.usersTabModel = store.usersTabModel.set("CliniciansAsKVPList", cliniciansAsKVPList.toImmutable());
        }
    }

    private getEmployeeInfo(response: ServerEvent, store: AdministrationStore): void {
        store.usersTabModel = this.ClearEmployeeStuff(store);

        let employeeResponse: Employee = response.Data["EmployeeInfo"];
        cacheStore.RebindSingleEmployeeToCache(employeeResponse); // everytime we reload a full employee, refresh its info in cachestore employee cache and service cache
        let emp = EmployeeUIModel.fromCompositeObject(employeeResponse);

        if (emp.Model.Contact_id == sessionStore.individualContactId)
            sessionStore.rebindCurrentEmployee(emp);

        this.refreshEmployeeSearchResult(EmployeeSearch.fromEmployee(emp), store);

        // and reset it in the edit model
        this.resetSearchResults(store, false);
        store.usersTabModel = this.resetUserModelEditView(store.usersTabModel, emp);

        if (emp != null) {
            emp.ProfileImageVersion = GuidHelper.NewGuid();
            store.usersTabModel = store.usersTabModel.set("SelectedEmployee", emp);
            store.usersTabModel = store.usersTabModel.set("EditedUser", emp);
        }
        // get orphanLabs
        if (emp.Model.Licences != null && emp.Model.Licences.length > 0)
            store.usersActions.getOrphanLabs(emp.getLicenseDtos(true));

        store.usersTabModel = store.usersTabModel.set("PDFSearchKeywords", emp.getArrayAllowedTags());
        let currentTagNames = store.usersTabModel.get("PDFSearchKeywords");
        store.usersTabModel = store.usersTabModel.set("SelectePDFTagIds", emp.getCurrentTagNames(currentTagNames));
    }

    private refreshEmployeeSearchResult(empSearch: EmployeeSearch, store: AdministrationStore): void {
        if (empSearch != null) {
            let usersSearchModel = store.usersTabModel.get("UsersSearchModel");
            let empPerson = Person.FromEmployeeSearch(empSearch);
            let currentSearchResults: Person[] = usersSearchModel.get("SearchEmployeeResult").toArray();
            let matches = currentSearchResults.filter((x: Person) => x.ContactFacetId == empPerson.ContactFacetId);

            if (matches.length > 0) {
                let idx = currentSearchResults.indexOf(matches[0]);
                currentSearchResults[idx] = empPerson;
            }
            else
                currentSearchResults.push(empPerson);

            usersSearchModel = usersSearchModel.set("SearchEmployeeResult", currentSearchResults.toImmutable());
            store.usersTabModel = store.usersTabModel.set("UsersSearchModel", usersSearchModel);
        }
    }

    private ClearEmployeeStuff(store: AdministrationStore): IUsersTabModel {
        store.usersTabModel = store.usersTabModel.set("IsCreateClickableTextOpen", false);
        store.usersTabModel = store.usersTabModel.set("ClickableTextSpecificUser", null);
        store.usersTabModel = store.usersTabModel.set("NewUsefulLink", null);
        return store.usersTabModel;
    }

    private saveProfileImage(response: ServerEvent, store: AdministrationStore): void {
        let selectedEmployee = store.usersTabModel.get("SelectedEmployee");
        if (selectedEmployee != null) {
            let newSelected = selectedEmployee.clone();
            newSelected.Model.ProfilePicture = response.Data.ProfilePicture;
            newSelected.ProfileImageVersion = GuidHelper.NewGuid();
            store.usersTabModel = store.usersTabModel.set("SelectedEmployee", newSelected);
        }
    }

    private getOrphanLabs(response: ServerEvent, store: AdministrationStore): void {
        const orphans: OrphanLabForwardDTO[] = response.Data.OrphanLabForwardDTOs;

        const employee = store.usersTabModel.get("SelectedEmployee");
        if (orphans && employee) {
            employee.Model.OrphanLabForwardDTOs = orphans;
            const employeeClone = employee.clone();
            store.usersTabModel = store.usersTabModel
                .set("SelectedEmployee", employeeClone)
                .set("EditedUser", employeeClone);
        }
    }

    private updateUser(response: ServerEvent, store: AdministrationStore): void {
        if (response.Data.ValidationErrors != null && response.Data.ValidationErrors.length > 0) {
            let errors: ErrorMessage[] = response.Data.ValidationErrors;
            var emailErrors = errors.filter(this.isEmailRelatedError);

            if (emailErrors && emailErrors.length) {
                let messages = emailErrors.map(p => p.Code !== "DuplicatedUserEmailInSystemError" ? cacheStore.GetString(p.Code) : p);
                sessionStore.showPopup(messages);
                return;
            }

            console.error(errors);
            sessionStore.showPopup(errors);
        }

        if (response.Data.Success) {
            // refresh edited user
            const selectedEmployee = store.usersTabModel.get("EditedUser");
            if (selectedEmployee) {
                store.usersActions.getEmployeeInfo(selectedEmployee.Model.ContactFacet_id);
                store.usersTabModel = store.usersTabModel.set("UserEditMode", UserEditMode.View);

                // can only change multifactor auth code delivery when editing your own account
                const isCurrentUser = cacheStore.GetCurrentEmployee().ContactFacet_id === selectedEmployee.Model.ContactFacet_id;
                if (isCurrentUser && !selectedEmployee.Model.AuthenticatorEnabled && sessionStore.AuthenticatorCodeDeliveryMethod !== AuthenticatorCodeDeliveryMethod.NotSelected) {
                    sessionStore.dispatch(LocalOperations.Settings.UpdateAuthenticatorCodeDeliveryMethod, { method: AuthenticatorCodeDeliveryMethod.NotSelected });
                }
            }
        }
    }

    private isEmailRelatedError(error: ErrorMessage): boolean {
        return error.Code && error.Code.indexOf("Email") > 0;
    }

    private updateConfigurationUser(response: ServerEvent, store: AdministrationStore): void {
        if (response.Data.Success) {
            let selectedEmployee = store.usersTabModel.get("EditedUser");

            store.usersTabModel = this.resetUserModelEditView(store.usersTabModel, selectedEmployee);

            // refresh user
            store.usersActions.getEmployeeInfo(selectedEmployee.Model.ContactFacet_id);
        }
    }

    private resetUserModelEditView(usersModel: IUsersTabModel, selectedUser: EmployeeUIModel): IUsersTabModel {
        usersModel = usersModel.set("SelectedEmployee", selectedUser);
        usersModel = usersModel.set("EditedUser", selectedUser);
        usersModel = usersModel.set("NewUsefulLink", "");
        usersModel = usersModel.set("showClickableTextFromAnotherUser", false);
        usersModel = usersModel.set("IsModifyUserNameMode", false);
        usersModel = usersModel.set("IsModifyUserTypeMode", false);
        usersModel = usersModel.set("IsModifyPasswordMode", false);
        usersModel = usersModel.set("IsModifyEmailMode", false);
        usersModel = usersModel.set("IsModifyPhoneMode", false);
        return usersModel;
    }

    private orphanLabHandler(response: ServerEventWithThen<any, (isSuccess: boolean) => void>, store: AdministrationStore): void {
        if (response.hasErrors()) {
            response.then?.(false);
            sessionStore.showPopup("Error while saving new forwarding rule.");
            return;
        }

        let employee: EmployeeUIModel = store.usersTabModel.get("EditedUser");
        if (employee.Model.Licences.any()) {
            window.setTimeout(() => {
                store.usersActions.getOrphanLabs(employee.getLicenseDtos(true));
            }, 10);
        }

        store.usersTabModel = store.usersTabModel.set("SelectedEmployee", employee.clone());
        store.usersTabModel = store.usersTabModel.set("EditedUser", employee.clone());

        response.then?.(true);
    }

    private updateEmployeeType(response: ServerEvent, store: AdministrationStore): void {
        const isSuccess: boolean = response.Data["IsSuccess"];
        const contactId = response.Data["ContactId"];
        const type = response.Data["Type"];
        const licenseOrganizationRuleId = response.Data["LicenseOrganizationRuleId"] ?? null;
        const licenseOrganizationSpecializationId = response.Data["LicenseOrganizationSpecializationId"] ?? null;

        if (isSuccess) {
            let usersSearchModel = store.usersTabModel.get("UsersSearchModel");
            const employees = usersSearchModel.get("SearchEmployeeResult").toArray();
            const employee = employees.filter((e: Person) => e.EmployeeSearchModel.Contact_id == contactId).firstOrDefault();

            if (employee != null) {
                employee.EmployeeSearchModel.Type = type;
                if (!employee.EmployeeSearchModel.IssuingLicenseOrganizationRuleId)
                    employee.EmployeeSearchModel.IssuingLicenseOrganizationRuleId = licenseOrganizationRuleId;
            }

            usersSearchModel = usersSearchModel.set("SearchEmployeeResult", Immutable.List(employees));
            store.usersTabModel = store.usersTabModel.set("UsersSearchModel", usersSearchModel);

            const selectedEmployee = store.usersTabModel.get("SelectedEmployee");
            selectedEmployee.Model.Type = type;

            selectedEmployee.setIssuingOrgRule(licenseOrganizationRuleId);
            selectedEmployee.Model.LicenseOrganizationSpecializationId = licenseOrganizationSpecializationId;

            if (type !== EmployeeType.Practician)
                selectedEmployee.Model.ServicesIDsOffered = [];

            store.usersTabModel = store.usersTabModel
                .set("SelectedEmployee", selectedEmployee.clone())
                .set("EditedUser", selectedEmployee.clone())
                .set("IsModifyUserTypeMode", false);

            if (store.usersTabModel.get("SelectedSubmenuItem") == UsersSubmenuEnum.Practice && type === EmployeeType.Assistant) {
                store.usersTabModel = store.usersTabModel
                    .set("SelectedSubmenuItem", UsersSubmenuEnum.Demography)
                    .set("UserEditMode", UserEditMode.View);
            }
            else
                store.usersTabModel = store.usersTabModel.set("UserEditMode", UserEditMode.View);

            store.usersActions.updateConfigurationEmployee(selectedEmployee.Model);
        }
        else
            sessionStore.showPopup(response.Errors);
    }

    private getClickableTextSpecificUser(response: ServerEvent, store: AdministrationStore): void {
        let textList = response.Data["TextList"];
        let IsImportDeleteTexts = response.Data["IsImportDeleteTexts"];
        let IsRefresh = store.usersTabModel.get("IsRefreshClickableText");

        let editedEmployee = store.usersTabModel.get("EditedUser");
        editedEmployee.Model.TextList = textList;

        if (editedEmployee.Model.TextList && editedEmployee.Model.TextList.length > 0)
            editedEmployee.Model.TextList.sortByProperty(x => x.TabOrderIndex);

        store.usersTabModel = store.usersTabModel.set("EditedUser", editedEmployee.clone());

        if (!IsRefresh && !IsImportDeleteTexts) {
            store.usersTabModel = store.usersTabModel.set("ClickableTextSpecificUser", textList);
            if (store.usersTabModel.get("UserEditMode") == UserEditMode.Edit)
                store.usersActions.createListOfClickableText(editedEmployee.Model.TextList.sortByProperty(x => x.TabOrderIndex), editedEmployee.Model.Contact_id);
        }

        else
            store.usersTabModel = store.usersTabModel.set("IsRefreshClickableText", false);
    }

    private createListOfClickableText(response: ServerEvent, store: AdministrationStore): void {
        let isSuccess: boolean = response.Data["IsSuccess"];
        store.usersTabModel = store.usersTabModel.set("ClickableTextSpecificUser", null);

        if (isSuccess)
            store.usersTabModel = store.usersTabModel.set("IsCreateClickableTextOpen", false);
    }

    private createEmployee<T>(response: ServerEventWithThen<T, () => void>, store: AdministrationStore): void {
        if (response.Data.ValidationErrors != null && response.Data.ValidationErrors.length > 0) {
            let errors: ErrorMessage[] = response.Data.ValidationErrors;
            var emailErrors = errors.filter(this.isEmailRelatedError);

            if (emailErrors && emailErrors.length) {
                let messages = emailErrors.map(p => p.Code !== "DuplicatedUserEmailInSystemError" ? cacheStore.GetString(p.Code) : p);
                sessionStore.showPopup(messages);
            }
            else
                sessionStore.showPopup(errors);

            response.then?.();

            return;
        }

        if (response.Data.Success) {
            this.resetSearchResults(store, true);
            let editUser = store.usersTabModel.get("EditedUser");
            editUser.Model.Contact_id = response.Data.CreatedContactId;
            editUser.Model.ContactFacet_id = response.Data.CreatedContactFacetId;
            store.usersTabModel = store.usersTabModel.set("EditedUser", editUser);

            window.setTimeout(() => store.usersActions.getEmployeeInfo(response.Data.CreatedContactFacetId), 200);

            // set default view back to View & Demography
            store.usersTabModel = store.usersTabModel.set("UserEditMode", UserEditMode.View);
            store.usersTabModel = store.usersTabModel.set("SelectedSubmenuItem", UsersSubmenuEnum.Demography);
        }
    }

    private resetSearchResults(store: AdministrationStore, clearSearchString: boolean = false): void {
        // clear search criterias and select just created employee
        let searchModel = store.usersTabModel.get("UsersSearchModel");
        if (clearSearchString)
            searchModel = searchModel.set("SearchText", "");
        store.usersTabModel = store.usersTabModel.set("UsersSearchModel", searchModel);
    }

    private deleteProfilePic(response: ServerEvent, store: AdministrationStore): void {
        if (response.Data.Success) {
            let selectedEmployee = store.usersTabModel.get("SelectedEmployee");
            selectedEmployee.Model.ProfilePicture = null;

            store.usersTabModel = store.usersTabModel.set("SelectedEmployee", selectedEmployee.clone());
            store.usersTabModel = store.usersTabModel.set("EditedUser", selectedEmployee.clone());
        }
    }

    private deleteClickableTextsection(response: ServerEvent, store: AdministrationStore): void {
        let isSuccess: boolean = response.Data["IsSuccess"];
        if (isSuccess) {
            let editUser = store.usersTabModel.get("EditedUser");
            store.usersActions.getCliackableTextSpecificUser(editUser.Model.ContactFacet_id, true);
        }
    }

    private getClickableTextForSpecificContext(response: ServerEvent, store: AdministrationStore): void {
        let tabNames: string[] = response.Data["tabNames"];
        if (tabNames != null)
            store.usersTabModel = store.usersTabModel.set("AllTabNamesClickableTextForSpecificContext", tabNames);

        let context = store.usersTabModel.get("ContextForImportClickableText");
        let langClickableText = store.usersTabModel.get("LanguageOptionClickableText");
        let lang = sessionStore.getLanguageStringFromEnum(langClickableText);

        store.usersActions.getAllUsersWithClickableText(context, lang);
    }

    private getClickableTextDetails(response: ServerEvent, store: AdministrationStore): void {
        let textList: ClickableTextItem[] = response.Data["TextList"];

        if (textList != null)
            store.usersTabModel = store.usersTabModel.set("AllListOfClickableTextDetails", textList);
    }

    private importClickableText(response: ServerEvent, store: AdministrationStore): void {
        let isSuccess: boolean = response.Data["IsSuccess"];
        if (isSuccess) {
            let editUser = store.usersTabModel.get("EditedUser");
            store.usersActions.getCliackableTextSpecificUser(editUser.Model.ContactFacet_id, true);
        }
    }

    private loadAllUsersWithClickableText(response: ServerEvent, store: AdministrationStore): void {
        let employeeResponseList: EmployeeSearch[] = response.Data["EmployeesResult"];
        let persons = employeeResponseList.map(x => Person.FromEmployeeSearch(x));

        if (employeeResponseList != null) {
            store.usersTabModel = store.usersTabModel.set("AllUsersWithClickableText", persons.sortByProperty(x => x.LastName));
            store.usersTabModel = store.usersTabModel.set("IsImportClickableTextOpen", true);
        }
    }

    private getClickableTextDetailsSpecificUserSpecificTab(response: ServerEvent, store: AdministrationStore): void {
        let textList: ClickableTextItem[] = response.Data["TextList"];
        if (textList != null)
            store.usersTabModel = store.usersTabModel.set("AllListOfClickableTextDetailsForSpecificUser", textList.sortByProperty(x => x.TabOrderIndex));
    }

    private getGroupInfo(response: ServerEvent, store: AdministrationStore): void {
        let employeeGroup: EmployeeGroup = response.Data["EmployeeGroup"];

        if (employeeGroup != null) {
            let editUser = store.usersTabModel.get("EditedUser");
            store.usersTabModel = store.usersTabModel.set("EditedUser", editUser.clone());
        }
    }

    private HandleResendEmailVerificationCodeResponse(response: ServerEvent, store: AdministrationStore): void {
        store.usersTabModel = store.usersTabModel.set("ResendEmailVerificationCodeInProgress", false);

        if (response.hasErrors())
            sessionStore.showPopup(response.Errors.map(x => cacheStore.GetString(x.Code)));
    }
}

export { AdministrationUsersResponseHandler };
